import { useState, FormEvent } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'

interface Organization {
  id: number
  name: string
}

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  admin: boolean
      super_admin?: boolean
  organization?: Organization
}

interface AdminUserEditProps {
  user: User
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Edit({ user }: AdminUserEditProps) {
  const { t } = useTranslation('admin')
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    admin: user.admin
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    router.patch(`/admin/users/${user.id}`, formData, {
      onFinish: () => setIsSubmitting(false),
      onSuccess: () => {
        // Redirect handled by controller
      }
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('users.index.title'), href: '/admin/users' }, { label: t('users.edit.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground">{t('users.edit.title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('users.edit.description')}
            </p>
          </div>
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('users.edit.info.title')}</CardTitle>
              <CardDescription>{t('users.edit.info.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">{t('users.edit.info.firstName')}</Label>
                    <Input
                      id="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name">{t('users.edit.info.lastName')}</Label>
                    <Input
                      id="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('users.edit.info.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization">{t('users.edit.info.organization')}</Label>
                  <Input
                    id="organization"
                    type="text"
                    value={user.organization?.name || t('users.edit.info.none')}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{t('users.edit.info.orgNote')}</p>
                </div>

                <div className="flex items-center space-x-2 pt-4 border-t">
                  <input
                    id="admin"
                    type="checkbox"
                    checked={formData.admin}
                    onChange={(e) => setFormData({ ...formData, admin: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-muted border-border rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="admin" className="font-normal cursor-pointer">
                    {t('users.edit.info.adminPrivileges')}
                  </Label>
                </div>

                <div className="flex justify-end gap-3 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.visit(`/admin/users/${user.id}`)}
                    disabled={isSubmitting}
                  >
                    {t('users.edit.buttons.cancel')}
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('users.edit.buttons.saving') : t('users.edit.buttons.saveChanges')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}




Edit.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Edit
